import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomer } from "../lib/customerAuth";
import { useCart } from "../lib/cart";
import { LogOut, Package, Truck, FileCheck, ShieldCheck, RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import usePageMeta from "../lib/usePageMeta";

export default function Account() {
  const { customer, checking, logout, authFetch } = useCustomer();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageMeta("My Account", "View your Peptigen Labs orders and dispatch updates.");

  useEffect(() => {
    if (!checking && !customer) navigate("/account/login");
  }, [checking, customer, navigate]);

  useEffect(() => {
    if (!customer) return;
    authFetch("/customers/me/orders")
      .then((d) => setOrders(d))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [customer, authFetch]);

  if (checking || !customer) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="pg-mono text-[#00C2D6] text-xs tracking-[0.3em]">LOADING ACCOUNT...</div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const reorder = (order) => {
    let added = 0;
    order.items.forEach((it) => {
      addItem(
        {
          id: it.product_id,
          sku: it.sku,
          name: it.name,
          strength: it.strength,
          price: it.price,
          category: it.sku === "PL-BACT-3" ? "Laboratory Reagents" : "",
        },
        it.quantity
      );
      added += it.quantity;
    });
    toast.success(`${added} item${added > 1 ? "s" : ""} added — review your basket`);
    navigate("/cart");
  };

  const totalSpent = orders
    .filter((o) => o.payment_status === "paid")
    .reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="account-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="pg-micro mb-2">// My Account</div>
          <h1 className="text-[#F7FAFC] text-4xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Hello, {customer.name?.split(" ")[0] || "Researcher"}
          </h1>
          <p className="text-[#9CA8B8] mt-1 text-sm">{customer.email}</p>
        </div>
        <button onClick={handleLogout} className="pg-btn-ghost" data-testid="account-logout">
          <LogOut size={14} /> Sign Out
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <Stat icon={Package} label="Total Orders" value={orders.length} />
        <Stat icon={Truck} label="In Transit" value={orders.filter((o) => o.status === "shipped").length} />
        <Stat icon={FileCheck} label="Delivered" value={orders.filter((o) => o.status === "delivered").length} />
        <Stat icon={ShieldCheck} label="Lifetime Spend" value={`£${totalSpent.toFixed(2)}`} accent="lime" />
      </div>

      <div className="pg-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="pg-micro mb-1">// Orders</div>
            <h2 className="text-[#F7FAFC] pg-heading text-xl" style={{ fontFamily: "'Rajdhani'" }}>
              Your order history
            </h2>
          </div>
          <Link to="/products" className="pg-btn-ghost text-xs">Shop catalogue</Link>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#9CA8B8] pg-mono text-xs tracking-[0.3em]">LOADING ORDERS...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package size={32} className="text-[#5EEAD4] mx-auto mb-3" />
            <div className="text-[#F7FAFC] pg-heading text-lg mb-1" style={{ fontFamily: "'Rajdhani'" }}>
              No orders yet
            </div>
            <p className="text-[#9CA8B8] text-sm mb-6">When you place an order it'll appear here.</p>
            <Link to="/currently-available" className="pg-btn-primary">Browse In-Stock</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="p-4 border border-[#00C2D6]/20 rounded-sm" data-testid={`account-order-${o.order_number}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="pg-micro mb-1">Order</div>
                    <div className="text-[#F7FAFC] pg-mono text-sm">{o.order_number}</div>
                    <div className="text-[#9CA8B8] text-xs mt-1">{o.created_at?.slice(0, 10)}</div>
                  </div>
                  <div className="text-right">
                    <span className={`pg-badge-${o.payment_status === 'paid' ? 'stock' : 'cyan'}`}>
                      {o.status.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <div className="text-[#A6FF00] pg-mono mt-1">£{o.total.toFixed(2)}</div>
                  </div>
                </div>
                <div className="space-y-1 text-sm mb-3">
                  {o.items.map((it) => (
                    <div key={it.sku} className="flex justify-between">
                      <span className="text-[#F7FAFC]">{it.name} {it.strength} × {it.quantity}</span>
                      <span className="text-[#9CA8B8] pg-mono">£{(it.price * it.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {o.tracking_number && (
                  <div className="flex items-center gap-2 text-xs pg-mono uppercase tracking-[0.18em] text-[#5EEAD4] pt-3 border-t border-[#00C2D6]/15">
                    <Truck size={12} /> Tracking: {o.tracking_number} {o.courier && `· ${o.courier}`}
                  </div>
                )}
                <div className="flex justify-end pt-3 mt-3 border-t border-[#00C2D6]/15">
                  <button
                    type="button"
                    onClick={() => reorder(o)}
                    className="pg-btn-primary text-xs flex items-center gap-2"
                    data-testid={`reorder-${o.order_number}`}
                  >
                    <RotateCcw size={12} /> Reorder in 1 click
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="pg-card p-5">
      <Icon size={16} className={accent === "lime" ? "text-[#A6FF00]" : "text-[#5EEAD4]"} />
      <div className="pg-micro mt-3 mb-1">{label}</div>
      <div
        className={`pg-display text-2xl ${accent === "lime" ? "text-[#A6FF00]" : "text-[#F7FAFC]"}`}
        style={{ fontFamily: "'Exo 2'" }}
      >
        {value}
      </div>
    </div>
  );
}
